import React from "react";

interface MetricCardProps {
  label: string;
  value: number | string;
  accent?: "green" | "red" | "orange" | "default";
  icon?: React.ReactNode;
  helperText?: string;
}

export const MetricCard = ({
  label,
  value,
  accent = "default",
  icon,
  helperText = 'Units',
}: MetricCardProps) => {
  // One tone drives the left border, icon tile and hover glow together, so a
  // caller only picks an accent (and a lucide icon) and never hardcodes colours.
  // "default" follows --primary (blue inside .scTheme); orange is the theme's
  // warm accent; green/red stay semantic for success and failure.
  const styles = {
    default: {
      card: "border-l-4 border-l-[var(--primary)] border-y-slate-200 border-r-slate-200 bg-gradient-to-br from-white via-white to-[var(--primaryExtraLight)]",
      iconContainer:
        "bg-[var(--primaryLight)] text-[var(--primary)] border-[rgb(var(--primaryRgb)/0.2)]",
      valueText: "text-[var(--headingTextColor)]",
      glow: "bg-[rgb(var(--primaryRgb)/0.07)]",
    },
    orange: {
      card: "border-l-4 border-l-[var(--actionOrange)] border-y-slate-200 border-r-slate-200 bg-gradient-to-br from-white via-white to-orange-50/40",
      iconContainer:
        "bg-[#FFF4E5] text-[var(--actionOrange)] border-[rgb(var(--actionOrangeRgb)/0.25)]",
      valueText: "text-[var(--headingTextColor)]",
      glow: "bg-[rgb(var(--actionOrangeRgb)/0.1)]",
    },
    green: {
      card: "border-l-4 border-l-emerald-500 border-y-slate-200 border-r-slate-200 bg-gradient-to-br from-white via-white to-emerald-50/40",
      iconContainer: "bg-emerald-50 text-emerald-600 border-emerald-200/70",
      valueText: "text-emerald-700",
      glow: "bg-emerald-500/10",
    },
    red: {
      card: "border-l-4 border-l-rose-500 border-y-slate-200 border-r-slate-200 bg-gradient-to-br from-white via-white to-rose-50/40",
      iconContainer: "bg-rose-50 text-rose-600 border-rose-200/70",
      valueText: "text-rose-700",
      glow: "bg-rose-500/10",
    },
  }[accent];

  return (
    <div
      className={`w-full min-h-[96px] bg-white border rounded-2xl p-4 flex items-center justify-between shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm relative overflow-hidden group ${styles.card}`}
    >
      {/* Dynamic Background Design Accent Glow */}
      <div className={`absolute -right-4 -bottom-4 w-16 h-16 ${styles.glow} rounded-full blur-lg group-hover:scale-125 transition-transform duration-300`} />

      {/* Content Metrics */}
      <div className="space-y-1.5 min-w-0 z-10">
        <span className="text-[10px] font-bold text-slate-400 tracking-widest block font-sans antialiased">
          {label}
        </span>
        <div className="flex items-baseline gap-1">
          <span
            className={`text-3xl font-black font-mono tracking-tight ${styles.valueText}`}
          >
            {typeof value === "number" ? value.toLocaleString() : value}
          </span>
          <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider ml-0.5">
            {helperText}
          </span>
        </div>
      </div>

      {/* Structured Icon Container Panel */}
      {icon && (
        <div
          className={`p-2.5 rounded-xl border flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 duration-300 shadow-2xs ${styles.iconContainer}`}
        >
          {/* CRITICAL UX FIX: Added [&>svg]:stroke-current to map the SVG lines directly to your dynamic text color definitions */}
          <div className="w-5 h-5 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:stroke-current [&>svg]:fill-none">
            {icon}
          </div>
        </div>
      )}
    </div>
  );
};